import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useOnboarding, useForwardAction } from '@/components/onboarding'
import { providerApiClient } from '@/lib/api'
import { onboardingKeys } from '@/lib/queryKeys'
import {
  STRIPE_ACCOUNT_STATUSES,
  stripeConnectStepDataSchema,
  type StripeAccountStatus,
} from '@/schemas/onboarding-stripe'
import type { StripeConnectStepData } from '@/types/onboarding'

const STEP_INDEX = 3

function getStoredData(stepData: Record<string, unknown>): StripeConnectStepData | null {
  const stored = stepData[STEP_INDEX]
  if (!stored) return null
  const parsed = stripeConnectStepDataSchema.safeParse(stored)
  return parsed.success ? parsed.data : null
}

function isStripeAccountStatus(value: string | null): value is StripeAccountStatus {
  return value !== null && (STRIPE_ACCOUNT_STATUSES as readonly string[]).includes(value)
}

function StatusCard({ status }: { status: StripeAccountStatus }) {
  if (status === 'verified') {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
        <div className="flex items-center gap-2">
          <Badge className="border-transparent bg-green-100 text-green-800 hover:bg-green-100">
            Verified
          </Badge>
          <span className="text-sm font-medium text-green-900">
            Your Stripe account is ready to accept payments.
          </span>
        </div>
      </div>
    )
  }

  if (status === 'pending') {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2">
        <div className="flex items-center gap-2">
          <Badge className="border-transparent bg-amber-100 text-amber-800 hover:bg-amber-100">
            Pending
          </Badge>
          <span className="text-sm font-medium text-amber-900">Under review</span>
        </div>
        <p className="text-sm text-amber-900">
          Stripe is reviewing your information. You can continue setup and we&apos;ll notify
          you when verification is complete.
        </p>
      </div>
    )
  }

  if (status === 'restricted') {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-2">
        <div className="flex items-center gap-2">
          <Badge className="border-transparent bg-red-100 text-red-800 hover:bg-red-100">
            Restricted
          </Badge>
          <span className="text-sm font-medium text-red-900">
            Additional information required
          </span>
        </div>
        <p className="text-sm text-red-900">
          Stripe needs additional information before your account is fully verified.
        </p>
        <a
          href="#"
          className="inline-block text-sm font-medium text-red-700 underline"
          onClick={(e) => e.preventDefault()}
        >
          Resolve with Stripe →
        </a>
      </div>
    )
  }

  return null
}

export function StripeConnectStep() {
  const { state, setStepData, markCompleted, goToStep } = useOnboarding()
  const [searchParams, setSearchParams] = useSearchParams()

  const stored = getStoredData(state.stepData)
  const [status, setStatus] = useState<StripeAccountStatus>(
    () => stored?.status ?? 'not_connected',
  )

  // On mount (and on URL change), read the Stripe return status from the URL
  // query param. This mirrors a real Stripe hosted-onboarding redirect where
  // Stripe sends the user back to our return URL carrying the account status.
  useEffect(() => {
    const fromUrl = searchParams.get('stripe_status')
    if (isStripeAccountStatus(fromUrl)) {
      setStatus(fromUrl)
      setSearchParams(
        (prev) => {
          prev.delete('stripe_status')
          return prev
        },
        { replace: true },
      )
    }
  }, [searchParams, setSearchParams])

  // Fall back to server-reported status when we don't already have a local
  // value. Avoids clobbering a URL-supplied or user-submitted status.
  const { data: serverStatus } = useQuery({
    queryKey: onboardingKeys.stripeStatus(),
    queryFn: () => providerApiClient.getStripeAccountStatus(),
    staleTime: 30 * 1000,
  })

  useEffect(() => {
    if (!serverStatus) return
    if (status === 'not_connected' && serverStatus.status !== 'not_connected') {
      setStatus(serverStatus.status)
    }
  }, [serverStatus, status])

  const createLink = useMutation({
    mutationFn: () => providerApiClient.createStripeAccountLink(),
    onSuccess: ({ url }) => {
      window.location.assign(url)
    },
    onError: () => {
      toast.error('Could not start Stripe onboarding. Please try again.')
    },
  })

  const handleSubmit = () => {
    setStepData(STEP_INDEX, { status } satisfies StripeConnectStepData)
    markCompleted(STEP_INDEX)
    goToStep(STEP_INDEX + 1)
  }

  const canContinue = status !== 'not_connected'

  useForwardAction({
    label: 'Continue',
    disabled: !canContinue,
    onClick: handleSubmit,
  })

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Payments</h2>
        <p className="mt-1 text-sm text-slate-600">
          Connect a Stripe account to accept payments from patients. Stripe handles
          identity verification and bank account linking — Determa never sees your
          banking details.
        </p>
      </div>

      {status === 'not_connected' ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 space-y-4">
          <div>
            <h3 className="text-base font-medium text-slate-900">Set up payments</h3>
            <p className="mt-1 text-sm text-slate-600">
              You&apos;ll be redirected to Stripe to verify your identity and link a
              bank account. This usually takes a few minutes.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => createLink.mutate()}
            disabled={createLink.isPending}
          >
            {createLink.isPending ? 'Redirecting…' : 'Connect with Stripe'}
          </Button>
        </div>
      ) : (
        <StatusCard status={status} />
      )}
    </div>
  )
}
